/**
 * Attribute maps for product_variants.attributes (JSONB).
 * Keys sorted lexicographically — matches DB uniqueness and process-order semantics.
 */

import { normalizeOptionKey } from '@/lib/aliexpress-sku-attrs'

/** Storefront / DS: compare option values without breaking on extra spaces or casing. */
export function normalizeStorefrontOptionValue(s: string | null | undefined): string {
  return String(s ?? '').trim().toLowerCase()
}

/**
 * Map option dimension labels to a single token so `Color` / `Colour` / `Couleur` match
 * the same axis as persisted variant.attributes (import may use any of these).
 */
function optionAxisMatchKey(key: string): string {
  const n = normalizeOptionKey(key)
    .toLowerCase()
    .replace(/\s+/g, ' ')
  if (n === 'colour' || n === 'couleur') return 'color'
  if (n === 'taille') return 'size'
  return n
}

/**
 * Read a variant attribute by option name; key match uses {@link optionAxisMatchKey}
 * plus case-insensitive fallback on the raw key.
 * Returns trimmed display value, or undefined if missing/empty.
 */
export function getVariantAttributeValue(
  variantAttrs: Record<string, string>,
  optionKey: string,
): string | undefined {
  if (!String(optionKey ?? '').trim()) return undefined
  const wantAxis = optionAxisMatchKey(optionKey)
  const wantRaw = normalizeStorefrontOptionValue(optionKey)
  if (!wantAxis && !wantRaw) return undefined
  for (const [k, v] of Object.entries(variantAttrs || {})) {
    if (k.startsWith('_')) continue
    const axis = optionAxisMatchKey(k)
    const raw = normalizeStorefrontOptionValue(k)
    const keyMatch =
      (wantAxis && axis && wantAxis === axis) || (wantRaw && raw && wantRaw === raw)
    if (!keyMatch) continue
    const t = String(v ?? '').trim()
    return t === '' ? undefined : t
  }
  return undefined
}

export function storefrontOptionValuesEqual(a: string | undefined, b: string | undefined): boolean {
  return normalizeStorefrontOptionValue(a) === normalizeStorefrontOptionValue(b)
}

export function normalizeAttributes(attrs: Record<string, string>): Record<string, string> {
  if (!attrs || typeof attrs !== 'object') return {}
  return Object.fromEntries(Object.entries(attrs).sort(([a], [b]) => a.localeCompare(b)))
}

export function attributesKey(attrs: Record<string, string>): string {
  return JSON.stringify(normalizeAttributes(attrs))
}

/** Strip keys used only for DB disambiguation (leading `_`), e.g. `__ae_matrix`. */
export function stripInternalAttributeKeys(attrs: Record<string, string>): Record<string, string> {
  return normalizeAttributes(
    Object.fromEntries(Object.entries(attrs || {}).filter(([k]) => !k.startsWith('_'))),
  )
}

/** PDP / quick view: drop internal keys and empty values before matching or persisting selection. */
export function sanitizeStorefrontSelection(s: Record<string, string>): Record<string, string> {
  const stripped = stripInternalAttributeKeys(s)
  return normalizeAttributes(
    Object.fromEntries(Object.entries(stripped).filter(([, v]) => String(v ?? '').trim() !== '')),
  )
}

/**
 * Shopper-selected options (subset) match a variant row that may carry extra internal keys.
 */
export function variantAttributesMatchSelection(
  variantAttrs: Record<string, string>,
  shopperSelected: Record<string, string>,
): boolean {
  const s = sanitizeStorefrontSelection(shopperSelected)
  if (Object.keys(s).length === 0) return false
  for (const [k, val] of Object.entries(s)) {
    const vv = getVariantAttributeValue(variantAttrs, k)
    if (vv === undefined || !storefrontOptionValuesEqual(vv, val)) return false
  }
  return true
}

export function attributesEqual(a: Record<string, string>, b: Record<string, string>): boolean {
  return attributesKey(a) === attributesKey(b)
}
